import { measureLatency } from '../../gctl-measure';
import { VideoFields, Video } from '../../models/components/schemas/video/Video';
import {
  Select,
  NetworkError,
  deepCamelCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetAlbumVideoFields = readonly VideoFields[];

/** Options for GET /albums/{album_id}/videos/{video_id} */
export type GetAlbumVideoOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /albums/{album_id}/videos/{video_id} */
  query?: {
    /**
     * The hashed password of the showcase from the desktop cookie.
     */
    hashedPass?: string;

    /**
     * The password of the showcase.
     */
    password?: string;
  };
};

/** Result type for GET /albums/{album_id}/videos/{video_id} */
export type GetAlbumVideoResult<Paths extends readonly string[]> = Select<Video, Paths>;

/**
 * Get a specific video in a showcase
 * This method returns a single video belonging to the specified showcase. The authenticated user must be the owner of the showcase.
 */
export async function getAlbumVideo<Paths extends GetAlbumVideoFields>({
  baseUrl,
  select,
  where: { albumId, videoId },
  query,
  ...options
}: GetAlbumVideoOptions<Paths>): Promise<GetAlbumVideoResult<Paths>> {
  return measureLatency('getAlbumVideo', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/albums/${albumId}/videos/${videoId}?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetAlbumVideoResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PutAlbumVideoFields = readonly string[];

/** Options for PUT /albums/{album_id}/videos/{video_id} */
export type PutAlbumVideoOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
    /**
     * The ID of the video.
     */
    videoId: number;
  };
};

/** Result type for PUT /albums/{album_id}/videos/{video_id} */
export type PutAlbumVideoResult<Paths extends readonly string[]> = unknown;

/**
 * Add a specific video to a showcase
 * This method adds a single video to the specified showcase. The authenticated user must be the owner of the showcase.
 */
export async function putAlbumVideo<Paths extends PutAlbumVideoFields>({
  baseUrl,
  where: { albumId, videoId },
  ...options
}: PutAlbumVideoOptions<Paths>): Promise<PutAlbumVideoResult<Paths>> {
  return measureLatency('putAlbumVideo', 'PUT', async () => {
    let res = await fetch(`${baseUrl}/albums/${albumId}/videos/${videoId}`, {
      ...options,
      method: 'PUT',
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PutAlbumVideoResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type DeleteAlbumVideoFields = readonly string[];

/** Options for DELETE /albums/{album_id}/videos/{video_id} */
export type DeleteAlbumVideoOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
    /**
     * The ID of the video.
     */
    videoId: number;
  };
};

/** Result type for DELETE /albums/{album_id}/videos/{video_id} */
export type DeleteAlbumVideoResult<Paths extends readonly string[]> = unknown;

/**
 * Remove a video from a showcase
 * This method removes the specified video from its showcase. The authenticated user must be the owner of the showcase.
 */
export async function deleteAlbumVideo<Paths extends DeleteAlbumVideoFields>({
  baseUrl,
  where: { albumId, videoId },
  ...options
}: DeleteAlbumVideoOptions<Paths>): Promise<DeleteAlbumVideoResult<Paths>> {
  return measureLatency('deleteAlbumVideo', 'DELETE', async () => {
    let res = await fetch(`${baseUrl}/albums/${albumId}/videos/${videoId}`, {
      ...options,
      method: 'DELETE',
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as DeleteAlbumVideoResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
